import { Combobox } from "@kobalte/core/combobox"
import { type AccentHue, colorize } from "@repo/fika-design-system/color"
import { createEffect, createMemo, createSignal, For, Show } from "solid-js"
import { pillRecipe } from "../patterns/pills.css"
import { Icon } from "./icon"
import {
  comboboxContentClass,
  comboboxControlClass,
  comboboxFieldClass,
  comboboxInputClass,
  comboboxItemClass,
  comboboxItemIndicatorClass,
  comboboxLabelClass,
  comboboxListboxClass,
  comboboxRemoveButtonClass,
  comboboxTagsClass,
} from "./tagSelector.css"

type Props = {
  options: string[]
  placeholder?: string
  hue: AccentHue
  value: string[]
  onChange: (value: string[]) => void
}

export function TagSelector(props: Props) {
  const [inputValue, setInputValue] = createSignal<string | undefined>()
  const [inputRef, setInputRef] = createSignal<HTMLInputElement>()
  const [isOpen, setIsOpen] = createSignal(false)

  const options = createMemo(() => {
    const val = inputValue()
    const set = new Set(
      props.options.concat(props.value).concat(val ? [val] : []),
    )
    return Array.from(set)
  })

  createEffect(() => {
    const input = inputRef()
    const open = isOpen()
    const opts = options()
    const value = props.value

    if (input && open && opts.length > 0 && value) {
      setTimeout(() => {
        const homeEvent = new KeyboardEvent("keydown", {
          key: "Home",
          code: "Home",
          keyCode: 36,
          which: 36,
          bubbles: true,
          cancelable: true,
        })
        input.dispatchEvent(homeEvent)
      }, 10)
    }
  })

  return (
    <Combobox<string>
      multiple
      triggerMode="input"
      options={options()}
      value={props.value}
      onChange={props.onChange}
      placeholder={props.placeholder}
      class={comboboxFieldClass}
      onInputChange={value => setInputValue(value)}
      onOpenChange={setIsOpen}
      removeOnBackspace
      itemComponent={iProps => (
        <Show when={!props.value.includes(iProps.item.rawValue)}>
          <Combobox.Item
            item={iProps.item}
            class={comboboxItemClass({ hue: props.hue })}
          >
            <Combobox.ItemLabel>{iProps.item.rawValue}</Combobox.ItemLabel>
            <Combobox.ItemIndicator class={comboboxItemIndicatorClass}>
              <Icon name="check" />
            </Combobox.ItemIndicator>
          </Combobox.Item>
        </Show>
      )}
    >
      <Combobox.Label class={comboboxLabelClass}>
        <Icon name="tag" size="18" color={colorize(props.hue, "600")} />
        Tags:
      </Combobox.Label>
      <Combobox.Control<string> class={comboboxControlClass}>
        {state => (
          <div class={comboboxTagsClass}>
            <For each={state.selectedOptions()}>
              {option => (
                <span
                  onPointerDown={e => e.stopPropagation()}
                  class={pillRecipe({ hue: "sand", rounded: true })}
                >
                  {option}
                  <button
                    type="button"
                    class={comboboxRemoveButtonClass({ hue: "sand" })}
                    onClick={() => state.remove(option)}
                  >
                    <Icon name="x" size="18" />
                  </button>
                </span>
              )}
            </For>
            <Combobox.Input
              ref={setInputRef}
              class={comboboxInputClass}
              placeholder="Add a tag"
            />
          </div>
        )}
      </Combobox.Control>
      <Combobox.Portal>
        <Combobox.Content class={comboboxContentClass}>
          <Combobox.Listbox class={comboboxListboxClass} />
        </Combobox.Content>
      </Combobox.Portal>
    </Combobox>
  )
}
